// Core settlement building prefabs: house, tower, tavern, castle, temple,
// church, fortress, workshop, market, farm. Adapted from adventure-craft.
import { box, hollowBox } from './buildings_shared.js';
import type { StructurePrefab } from './types.js';

export const house: StructurePrefab = {
  name: 'Wooden House',
  blocks: [
    ...box(-2, 0, -2, 5, 1, 5, '#8b4513', 'plank'),
    ...box(-2, 1, -2, 5, 3, 1, '#deb887', 'plank'),
    ...box(-2, 1, 2, 5, 3, 1, '#deb887', 'plank'),
    ...box(-2, 1, -1, 1, 3, 3, '#deb887', 'plank'),
    ...box(2, 1, -1, 1, 3, 3, '#deb887', 'plank'),
    { x: 0, y: 1, z: 2, color: '#654321', type: 'wood' },
    { x: 0, y: 2, z: 2, color: '#654321', type: 'wood' },
    { x: -1, y: 2, z: 2, color: '#87ceeb', type: 'glass' },
    { x: 1, y: 2, z: 2, color: '#87ceeb', type: 'glass' },
    ...box(-3, 4, -3, 7, 1, 7, '#8b0000', 'brick'),
    ...box(-2, 5, -2, 5, 1, 5, '#8b0000', 'brick'),
    ...box(-1, 6, -1, 3, 1, 3, '#8b0000', 'brick'),
    { x: 0, y: 7, z: 0, color: '#8b0000', type: 'brick' },
  ],
};

export const tower: StructurePrefab = {
  name: 'Stone Tower',
  blocks: [
    ...box(-3, 0, -3, 7, 1, 7, '#808080', 'stone'),
    ...hollowBox(-3, 1, -3, 7, 10, 7, '#909090', 'stone'),
    ...box(-1, 1, -4, 3, 3, 1, '#808080', 'stone'),
    ...box(-4, 3, -1, 1, 2, 3, '#606060', 'stone'),
    ...hollowBox(-2, 11, -2, 5, 2, 5, '#808080', 'stone'),
    ...box(-2, 13, -2, 5, 1, 5, '#606060', 'stone'),
    { x: 0, y: 14, z: 0, color: '#ff4444', type: 'stone' },
  ],
};

export const tavern: StructurePrefab = {
  name: 'Tavern',
  blocks: [
    ...box(-3, 0, -3, 7, 1, 7, '#8b4513', 'plank'),
    ...hollowBox(-3, 1, -3, 7, 4, 7, '#deb887', 'plank'),
    ...box(-2, 1, 3, 5, 3, 1, '#deb887', 'plank'),
    { x: 0, y: 1, z: 3, color: '#654321', type: 'wood' },
    { x: 0, y: 2, z: 3, color: '#654321', type: 'wood' },
    ...box(-1, 2, 3, 3, 1, 1, '#87ceeb', 'glass'),
    ...box(-1, 2, -4, 1, 2, 1, '#87ceeb', 'glass'),
    ...box(-4, 3, -1, 1, 1, 3, '#87ceeb', 'glass'),
    ...box(-3, 5, -3, 7, 1, 7, '#8b0000', 'brick'),
    ...box(-1, 6, -1, 3, 1, 3, '#8b0000', 'brick'),
    { x: 3, y: 2, z: -1, color: '#ffd700', type: 'wood' },
  ],
};

export const smallCastle: StructurePrefab = {
  name: 'Small Castle',
  blocks: [
    ...box(-5, 0, -5, 11, 1, 11, '#808080', 'stone'),
    ...hollowBox(-5, 1, -5, 11, 6, 11, '#909090', 'stone'),
    ...box(-5, 1, 5, 11, 4, 1, '#787878', 'stone'),
    ...box(-5, 1, -6, 11, 4, 1, '#787878', 'stone'),
    ...box(-6, 1, -5, 1, 4, 11, '#787878', 'stone'),
    ...box(5, 1, -5, 1, 4, 11, '#787878', 'stone'),
    ...box(-6, 0, -6, 2, 8, 2, '#808080', 'stone'),
    ...box(4, 0, -6, 2, 8, 2, '#808080', 'stone'),
    ...box(-6, 0, 4, 2, 8, 2, '#808080', 'stone'),
    ...box(4, 0, 4, 2, 8, 2, '#808080', 'stone'),
    ...box(-2, 7, -2, 5, 2, 5, '#707070', 'stone'),
    ...box(-1, 9, -1, 3, 1, 3, '#606060', 'stone'),
    { x: 0, y: 10, z: 0, color: '#ffd700', type: 'stone' },
  ],
};

export const temple: StructurePrefab = {
  name: 'Temple',
  blocks: [
    ...box(-4, 0, -4, 9, 1, 9, '#e8e8e8', 'stone'),
    ...hollowBox(-4, 1, -4, 9, 6, 9, '#f5f5dc', 'stone'),
    ...box(-3, 1, -5, 7, 1, 1, '#d4d4aa', 'stone'),
    ...box(-5, 1, -3, 1, 5, 7, '#d4d4aa', 'stone'),
    ...box(4, 1, -3, 1, 5, 7, '#d4d4aa', 'stone'),
    ...box(-4, 7, -4, 9, 1, 9, '#ffd700', 'stone'),
    ...box(-2, 8, -2, 5, 1, 5, '#ffcc00', 'stone'),
    ...box(-1, 9, -1, 3, 2, 3, '#ffaa00', 'stone'),
    { x: 0, y: 11, z: 0, color: '#ffffff', type: 'stone' },
  ],
};

export const church: StructurePrefab = {
  name: 'Church',
  blocks: [
    ...box(-3, 0, -5, 7, 1, 11, '#808080', 'stone'),
    ...hollowBox(-3, 1, -5, 7, 5, 11, '#b0b0b0', 'stone'),
    ...box(-1, 1, -6, 3, 3, 1, '#707070', 'stone'),
    ...box(-2, 6, -4, 5, 1, 9, '#808080', 'stone'),
    ...box(-1, 7, -3, 3, 1, 7, '#909090', 'stone'),
    ...box(-1, 8, -2, 3, 1, 5, '#a0a0a0', 'stone'),
    ...box(0, 9, -1, 2, 3, 3, '#b0b0b0', 'stone'),
    { x: 1, y: 12, z: 0, color: '#ffd700', type: 'stone' },
    { x: -2, y: 2, z: 5, color: '#87ceeb', type: 'glass' },
    { x: 2, y: 2, z: 5, color: '#87ceeb', type: 'glass' },
    { x: -2, y: 3, z: 5, color: '#87ceeb', type: 'glass' },
    { x: 2, y: 3, z: 5, color: '#87ceeb', type: 'glass' },
  ],
};

export const fortress: StructurePrefab = {
  name: 'Fortress',
  blocks: [
    ...box(-6, 0, -6, 13, 1, 13, '#505050', 'stone'),
    ...hollowBox(-6, 1, -6, 13, 8, 13, '#686868', 'stone'),
    ...box(-7, 0, -7, 2, 12, 2, '#585858', 'stone'),
    ...box(6, 0, -7, 2, 12, 2, '#585858', 'stone'),
    ...box(-7, 0, 6, 2, 12, 2, '#585858', 'stone'),
    ...box(6, 0, 6, 2, 12, 2, '#585858', 'stone'),
    ...box(-7, 12, -7, 3, 1, 3, '#484848', 'stone'),
    ...box(5, 12, -7, 3, 1, 3, '#484848', 'stone'),
    ...box(-7, 12, 5, 3, 1, 3, '#484848', 'stone'),
    ...box(5, 12, 5, 3, 1, 3, '#484848', 'stone'),
    ...box(-3, 9, -3, 7, 2, 7, '#606060', 'stone'),
    ...box(-2, 11, -2, 5, 1, 5, '#585858', 'stone'),
  ],
};

export const workshop: StructurePrefab = {
  name: 'Workshop',
  blocks: [
    ...box(-3, 0, -3, 7, 1, 7, '#8b4513', 'plank'),
    ...box(-3, 1, -3, 7, 1, 7, '#a0522d', 'plank'),
    ...box(-3, 2, -3, 1, 3, 7, '#deb887', 'plank'),
    ...box(3, 2, -3, 1, 3, 7, '#deb887', 'plank'),
    ...box(-2, 2, -3, 5, 3, 1, '#deb887', 'plank'),
    ...box(-2, 2, 3, 5, 3, 1, '#deb887', 'plank'),
    ...box(-2, 5, -3, 5, 1, 7, '#8b4513', 'plank'),
    ...box(-1, 6, -2, 3, 1, 5, '#654321', 'wood'),
  ],
};

export const market: StructurePrefab = {
  name: 'Market',
  blocks: [
    ...box(-4, 0, -4, 9, 1, 9, '#c0c0c0', 'stone'),
    ...box(-4, 1, -4, 1, 4, 9, '#deb887', 'plank'),
    ...box(4, 1, -4, 1, 4, 9, '#deb887', 'plank'),
    ...box(-3, 1, -4, 7, 4, 1, '#deb887', 'plank'),
    ...box(-3, 1, 4, 7, 4, 1, '#deb887', 'plank'),
    ...box(-3, 5, -4, 7, 1, 9, '#8b4513', 'wood'),
    { x: -1, y: 2, z: -3, color: '#ffd700', type: 'wood' },
    { x: 1, y: 2, z: -3, color: '#ffd700', type: 'wood' },
    { x: -1, y: 2, z: 3, color: '#ffd700', type: 'wood' },
    { x: 1, y: 2, z: 3, color: '#ffd700', type: 'wood' },
  ],
};

export const farm: StructurePrefab = {
  name: 'Farm',
  blocks: [
    ...box(-3, 0, -3, 7, 1, 7, '#8b4513', 'wood'),
    ...box(-3, 1, -3, 1, 3, 7, '#deb887', 'plank'),
    ...box(3, 1, -3, 1, 3, 7, '#deb887', 'plank'),
    ...box(-2, 1, -3, 5, 3, 1, '#deb887', 'plank'),
    ...box(-2, 1, 3, 5, 3, 1, '#deb887', 'plank'),
    ...box(-2, 4, -3, 5, 1, 7, '#a0522d', 'wood'),
    ...box(-1, 5, -2, 3, 1, 5, '#8b4513', 'wood'),
    { x: 0, y: 6, z: 0, color: '#cd853f', type: 'wood' },
    { x: -2, y: 1, z: 1, color: '#ffd700', type: 'wood' },
    { x: 2, y: 1, z: 1, color: '#ffd700', type: 'wood' },
  ],
};
